import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import { apiLogger } from '@/utils/logger'

// API Configuration
const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? import.meta.env.VITE_API_BASE_URL

if (!API_BASE_URL) {
  if (import.meta.env.DEV) {
    console.warn('VITE_API_URL not set, using localhost:3000 for development')
  } else {
    throw new Error(
      'VITE_API_URL environment variable must be set for production builds'
    )
  }
}

const REQUEST_TIMEOUT = 30000 // 30 seconds

// Create base axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Skip token handling for refresh requests to avoid loops
    if (config.url?.includes('/auth/refresh')) {
      config.metadata = { startTime: new Date() }
      return config
    }

    let token = getStoredToken()

    // If token is expired, proactively refresh before sending the request
    if (!token && hasStoredRefreshToken()) {
      try {
        const refreshed = await attemptTokenRefresh()
        if (refreshed) {
          token = getStoredToken()
        }
      } catch {
        apiLogger.warn('Proactive token refresh failed')
      }
    }

    // Log API requests in development
    apiLogger.debug('API Request', {
      method: config.method,
      url: config.url,
      hasToken: !!token,
      tokenPrefix: token ? token.substring(0, 20) : 'none',
    })

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      apiLogger.debug('Added Authorization header', {
        token: `${token.substring(0, 20)}...`,
      })
    } else {
      apiLogger.warn('No token available for request')
    }

    // Don't set Content-Type for FormData - let browser handle it
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() }

    return config
  },
  (error) => {
    apiLogger.error('Request interceptor error', error)
    return Promise.reject(error)
  }
)

// Response interceptor for handling common responses and errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Track response time in development
    if (response.config.metadata?.startTime) {
      const duration = Date.now() - response.config.metadata.startTime.getTime()
      apiLogger.debug('API Response', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        duration: `${duration}ms`,
      })
    }

    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config

    // Handle token expiration (but exclude refresh endpoint to prevent infinite
    // loops, and exclude cross-backend requests — see isPlatformRequest). A 401
    // from a non-platform backend (e.g. the Go tracker API, reached via an
    // absolute URL) must not trigger a platform refresh/logout: refreshing the
    // platform token can't fix that backend's auth, and on refresh failure we
    // would log the user out of the entire platform over an unrelated 401.
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      isPlatformRequest(originalRequest.url)
    ) {
      originalRequest._retry = true

      try {
        // Attempt to refresh token
        const refreshed = await attemptTokenRefresh()

        if (refreshed) {
          // Retry original request with new token
          const newToken = getStoredToken()
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
          }
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        handleAuthenticationFailure()
        return Promise.reject(refreshError)
      }
    }

    // Handle other common errors
    if (error.response?.status === 403) {
      // Handle authorization errors
      apiLogger.error('Access forbidden', { data: error.response.data })
    }

    if (error.response && error.response.status >= 500) {
      // Handle server errors
      apiLogger.error('Server error', { data: error.response.data })
    }

    if (!error.response) {
      // Handle network errors
      apiLogger.error('Network error', error)
    }

    return Promise.reject(error)
  }
)

// A request targets the platform Rails API when its URL is relative (axios
// prepends apiClient.baseURL) or its absolute origin matches the platform API
// origin. Absolute URLs to a different origin — e.g. the embedded tracker's
// calls to the Go API (VITE_TRACKER_API_URL) — are NOT platform requests, so
// their 401s must not drive the platform refresh/logout flow.
function isPlatformRequest(url: string | undefined): boolean {
  if (!url) return true
  // Relative URL -> resolved against the platform baseURL -> platform request.
  if (!/^https?:\/\//i.test(url)) return true
  try {
    const requestOrigin = new URL(url).origin
    const platformOrigin = API_BASE_URL
      ? new URL(API_BASE_URL, window.location.origin).origin
      : window.location.origin
    return requestOrigin === platformOrigin
  } catch {
    // Unparseable URL — treat as platform to preserve existing behavior.
    return true
  }
}

// Helper functions for auth integration
function hasStoredRefreshToken(): boolean {
  try {
    const authData = localStorage.getItem('auth-storage')
    if (!authData) return false
    const parsed = JSON.parse(authData)
    return !!parsed.state?.tokens?.refreshToken
  } catch {
    return false
  }
}

function getStoredToken(): string | null {
  // Import auth store dynamically to avoid circular dependencies
  if (typeof window !== 'undefined') {
    try {
      // Try to get from Zustand persist storage first
      const authData = localStorage.getItem('auth-storage')
      if (authData) {
        const parsed = JSON.parse(authData)
        const token = parsed.state?.tokens?.accessToken
        const expiresAt = parsed.state?.tokens?.expiresAt

        // Check if token is expired
        if (token && expiresAt && Date.now() < expiresAt) {
          return token
        }
      }

      // Token expired or not found — return null so the 401 interceptor
      // triggers a proper refresh flow
      apiLogger.warn('No valid token available for request')
    } catch (error) {
      apiLogger.warn('Error reading auth token from storage', error as Error)
    }
  }
  return null
}

// Mutex to prevent concurrent token refresh attempts
let refreshPromise: Promise<boolean> | null = null
const REFRESH_TIMEOUT_MS = 30000 // 30 second timeout

async function attemptTokenRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise

  const timeoutPromise = new Promise<boolean>((_, reject) => {
    setTimeout(
      () => reject(new Error('Token refresh timeout')),
      REFRESH_TIMEOUT_MS
    )
  })

  refreshPromise = Promise.race([doTokenRefresh(), timeoutPromise]).finally(
    () => {
      refreshPromise = null
    }
  )

  return refreshPromise
}

async function doTokenRefresh(): Promise<boolean> {
  if (typeof window === 'undefined') return false

  try {
    // Get refresh token from storage
    const authData = localStorage.getItem('auth-storage')
    if (!authData) return false

    const parsed = JSON.parse(authData)
    const refreshToken = parsed.state?.tokens?.refreshToken

    if (!refreshToken) return false

    const organizationId = parsed.state?.organization?.id

    // Import auth service dynamically to avoid circular dependencies
    const { authService } = await import('./auth')

    // Attempt to refresh token, passing current org to preserve workspace context
    const response = await authService.refreshToken(
      refreshToken,
      organizationId
    )

    // Update stored tokens
    const updatedAuthData = {
      ...parsed,
      state: {
        ...parsed.state,
        tokens: {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          expiresAt: Date.now() + response.expiresIn * 1000,
        },
        authState: 'authenticated',
      },
    }

    localStorage.setItem('auth-storage', JSON.stringify(updatedAuthData))

    return true
  } catch (error) {
    apiLogger.error('Token refresh failed', error as Error)
    // Clear auth storage when refresh fails
    localStorage.removeItem('auth-storage')
    return false
  }
}

function handleAuthenticationFailure(): void {
  // Clear auth storage
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auth-storage')
  }

  // Redirect to login page
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// Generic API response wrapper
export interface ApiResponse<T = unknown> {
  data: T
  message?: string
  success: boolean
  errors?: string[]
}

export class ApiError extends Error {
  code?: string
  status?: number
  errors?: Record<string, string[]>

  constructor({
    message,
    code,
    status,
    errors,
  }: {
    message: string
    code?: string
    status?: number
    errors?: Record<string, string[]>
  }) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.errors = errors
  }
}

// Helper function to handle API responses
export const handleApiResponse = <T>(
  response: AxiosResponse<ApiResponse<T>>
): T => {
  apiLogger.debug('API Response processed', {
    status: response.status,
    success: response.data.success,
    hasData: !!response.data.data,
  })

  if (response.data.success) {
    return response.data.data
  }

  throw new Error(response.data.message || 'API request failed')
}

// Helper function to handle API errors
export const handleApiError = (error: unknown): ApiError => {
  apiLogger.error('API Error occurred', error as Error)

  // Type guard to check if it's an AxiosError
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError

    // Enhanced error logging for development
    apiLogger.error('Axios error details', {
      message: axiosError.message,
      code: axiosError.code,
      response: axiosError.response?.data,
      status: axiosError.response?.status,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
      },
    })

    if (axiosError.response?.data) {
      const errorData = axiosError.response.data as {
        message?: string
        code?: string
        errors?: Record<string, string[]>
        error?: string
      }

      return new ApiError({
        message: errorData.message || errorData.error || 'An error occurred',
        code: errorData.code,
        status: axiosError.response.status,
        errors: errorData.errors,
      })
    }

    return new ApiError({
      message: axiosError.message || 'Network error',
      status: 0,
    })
  }

  // Handle generic errors
  if (error instanceof Error) {
    return new ApiError({
      message: error.message || 'An error occurred',
      status: 0,
    })
  }

  return new ApiError({
    message: 'Unknown error occurred',
    status: 0,
  })
}

// Export types for use in other services
export type { AxiosRequestConfig, AxiosResponse, AxiosError }

// Add request retry configuration to axios config
declare module 'axios' {
  interface AxiosRequestConfig {
    _retry?: boolean
    metadata?: {
      startTime: Date
    }
  }
}
